import type { CategoryTemplate, ChecklistTemplate, Prompt, TemplateKind } from '../types'

const prompt = (id: string, label: string, help?: string): Prompt => ({ id, label, help })

const shared: Record<string, CategoryTemplate> = {
  metering: {
    id: 'metering',
    label: 'Metering & utilities',
    description: 'Physical meters, sub-meters, labels, coverage and visible monitoring arrangements.',
    prompts: [
      prompt('meter-locations', 'Can the principal meters and sub-meters be located and identified?'),
      prompt('meter-boundaries', 'Is it clear what each meter serves?'),
      prompt('meter-condition', 'Are labels, displays, pulse outputs and communications in usable condition?'),
      prompt('meter-gaps', 'Are significant systems or areas visibly missing useful sub-metering?'),
    ],
    dataPrompts: ['Meter ID', 'Utility', 'Reading', 'Unit', 'What it serves', 'Read date/time'],
  },
  management: {
    id: 'management',
    label: 'Operating practices',
    description: 'How people start, stop, schedule, monitor and maintain energy-using systems.',
    prompts: [
      prompt('responsibility', 'Who changes schedules, setpoints and overrides?'),
      prompt('shutdown', 'Are shutdown and start-up practices understood and followed?'),
      prompt('faults', 'Are recurring faults, alarms and comfort issues tracked through to resolution?'),
      prompt('out-of-hours', 'What remains on out of hours, and why?'),
    ],
    dataPrompts: ['Operating hours', 'Shutdown time', 'Responsible person', 'Observed exception'],
  },
  fabric: {
    id: 'fabric',
    label: 'Building fabric',
    description: 'Envelope condition, openings, insulation evidence and avoidable heat gains or losses.',
    prompts: [
      prompt('glazing', 'Check glazing type, condition, shading and obvious air leakage.'),
      prompt('doors', 'Check doors, loading bays, seals, automatic closers and opening practices.'),
      prompt('insulation', 'Look for roof/wall insulation evidence, thermal bridges and damaged fabric.'),
      prompt('constraints', 'Record access or heritage constraints that may affect improvements.'),
    ],
    dataPrompts: ['Approx. area', 'Construction', 'Age', 'U-value if known', 'Defect location'],
  },
  heating: {
    id: 'heating',
    label: 'Heating',
    description: 'Heat generation, distribution, condition, settings, schedules and zoning.',
    prompts: [
      prompt('plant', 'Record plant type, quantity, nameplates, age and observed condition.'),
      prompt('distribution', 'Inspect pumps, pipe insulation, valves, emitters, leaks and bypasses.'),
      prompt('settings', 'Capture actual setpoints, schedules, zoning and weather compensation.'),
      prompt('simultaneous', 'Look for simultaneous heating and cooling or heat in unoccupied areas.'),
    ],
    dataPrompts: ['Plant type', 'Quantity', 'Rated kW', 'Fuel', 'Setpoint °C', 'Run hours', 'Age'],
  },
  cooling: {
    id: 'cooling',
    label: 'Cooling & refrigeration',
    description: 'Comfort/process cooling, heat rejection, refrigeration and operating controls.',
    prompts: [
      prompt('cooling-plant', 'Identify chillers, splits, condensers, cold rooms and display cases.'),
      prompt('cooling-settings', 'Record setpoints, schedules, deadbands and observed load.'),
      prompt('heat-rejection', 'Inspect condenser condition, airflow and heat rejection location.'),
      prompt('doors-seals', 'Check cold-room doors, seals, defrost arrangements and door-open practice.'),
    ],
    dataPrompts: ['Equipment type', 'Rated kW', 'Setpoint °C', 'Refrigerant', 'Run hours', 'Inverter Y/N'],
  },
  ventilation: {
    id: 'ventilation',
    label: 'Ventilation & extraction',
    description: 'AHUs, fans, extraction, filters, dampers, heat recovery and demand control.',
    prompts: [
      prompt('ahu-condition', 'Inspect AHUs, filters, dampers, belts and visible maintenance condition.'),
      prompt('fan-control', 'Record fan schedules, speed control and whether demand follows occupancy/process.'),
      prompt('heat-recovery', 'Check heat recovery type, bypass, cleanliness and observed operation.'),
      prompt('left-running', 'Look for ventilation or extraction running without demand.'),
    ],
    dataPrompts: ['Unit/served area', 'Rated kW', 'Run hours', 'Control type', 'Filter condition'],
  },
  hotWater: {
    id: 'hot-water',
    label: 'Hot water',
    description: 'Generation, storage, distribution, temperatures, timers, leaks and insulation.',
    prompts: [
      prompt('dhw-source', 'Identify generation and storage serving domestic or process hot water.'),
      prompt('dhw-settings', 'Record actual temperature settings, timers and recirculation operation.'),
      prompt('dhw-losses', 'Inspect cylinder, pipe and trace-heating insulation and visible leaks.'),
      prompt('dhw-demand', 'Ask when and where hot water demand occurs.'),
    ],
    dataPrompts: ['Generation type', 'Storage litres', 'Setpoint °C', 'Run hours', 'Timer setting'],
  },
  controls: {
    id: 'controls',
    label: 'Controls & BMS',
    description: 'Schedules, sensors, zoning, overrides, alarms and whether control matches actual use.',
    prompts: [
      prompt('bms-coverage', 'Which systems and areas are visible or controllable in the BMS?'),
      prompt('schedule-check', 'Compare schedules with actual occupancy, production and seasonal need.'),
      prompt('override-check', 'Record active overrides, hand mode, disabled sensors and persistent alarms.'),
      prompt('setpoints', 'Capture setpoints and deadbands; do not infer inefficiency without evidence.'),
    ],
    dataPrompts: ['Controller/BMS make', 'Schedule', 'Setpoint', 'Deadband', 'Override', 'Last optimised'],
  },
  lighting: {
    id: 'lighting',
    label: 'Lighting',
    description: 'Lamp types, quantities, controls, daylight and unnecessary operation.',
    prompts: [
      prompt('lighting-types', 'Record representative lamp/fitting types and approximate quantities.'),
      prompt('lighting-controls', 'Check occupancy, daylight, timeclock and local switching arrangements.'),
      prompt('lighting-hours', 'Look for lighting on in unoccupied or naturally lit areas.'),
      prompt('external-lighting', 'Check external lighting schedules and photocell operation.'),
    ],
    dataPrompts: ['Fitting type', 'Quantity', 'Watts/fitting', 'Hours/day', 'Control type', 'Area served'],
  },
  ict: {
    id: 'ict',
    label: 'ICT & small power',
    description: 'Servers, comms rooms, UPS, office equipment and persistent plug loads.',
    prompts: [
      prompt('server-load', 'Identify server/comms/UPS equipment, cooling and operating hours.'),
      prompt('office-load', 'Look for PCs, displays, printers, vending and kitchen equipment left on.'),
      prompt('power-management', 'Ask whether sleep, shutdown and timer settings are centrally managed.'),
      prompt('local-cooling', 'Record dedicated cooling and temperature setpoints.'),
    ],
    dataPrompts: ['Equipment', 'Quantity', 'Rated/estimated kW', 'Hours', 'Power management'],
  },
  motors: {
    id: 'motors',
    label: 'Motors, pumps & fans',
    description: 'Motor-driven systems, load matching, throttling, controls and idle operation.',
    prompts: [
      prompt('motor-nameplate', 'Record representative nameplates, duty, age and operating mode.'),
      prompt('motor-control', 'Check VSDs, starters, sequencing, throttling and bypass arrangements.'),
      prompt('motor-loading', 'Ask operators about loading, duty cycle and periods of idle running.'),
      prompt('motor-condition', 'Note noise, heat, vibration, leaks or maintenance concerns if safely observable.'),
    ],
    dataPrompts: ['Asset ID', 'Rated kW', 'Duty', 'Hours', 'Load %', 'VSD Y/N', 'Flow/pressure if known'],
    safetyNote: 'Do not remove guards or open panels. Use nameplates and interviews where measurement is unsafe.',
  },
  compressedAir: {
    id: 'compressed-air',
    label: 'Compressed air',
    description: 'Compressors, pressure, sequencing, leaks, treatment, distribution and end uses.',
    prompts: [
      prompt('compressor-plant', 'Record compressors, dryers, receivers and control/sequencing arrangements.'),
      prompt('air-pressure', 'Capture pressure setpoints and any known pressure-sensitive end uses.'),
      prompt('air-leaks', 'Listen for leaks where safe and note location/severity without disrupting production.'),
      prompt('air-misuse', 'Look for open blowing, cooling, vacuum generation or idle production supply.'),
    ],
    dataPrompts: ['Compressor ID', 'Rated kW', 'Pressure bar', 'Loaded hours', 'Unloaded hours', 'Leak location'],
    safetyNote: 'Follow site safety and do not interfere with pressurised equipment.',
  },
  process: {
    id: 'process',
    label: 'Process & specialist equipment',
    description: 'Production sequence, batch cycles, idle modes, specialist loads and operator practice.',
    prompts: [
      prompt('process-sequence', 'Understand the production sequence, bottlenecks and energy-intensive stages.'),
      prompt('process-idle', 'Record idle, standby, warm-up and shutdown behaviour.'),
      prompt('process-loading', 'Ask whether batches and equipment are loaded or scheduled efficiently.'),
      prompt('process-constraints', 'Capture quality, safety, hygiene and production constraints.'),
    ],
    dataPrompts: ['Asset/process', 'Rated kW', 'Cycle time', 'Hours/shift', 'Idle mode', 'Throughput context'],
    safetyNote: 'Do not remove guards. Use operator interviews and nameplates where direct inspection is unsafe.',
  },
  processHeat: {
    id: 'process-heat',
    label: 'Steam & process heat',
    description: 'Boilers, ovens, kilns, furnaces, steam distribution, condensate and insulation.',
    prompts: [
      prompt('heat-plant', 'Record boiler/oven/kiln/furnace type, capacity, fuel and actual settings.'),
      prompt('heat-cycle', 'Capture warm-up, production, idle and shutdown cycles.'),
      prompt('steam-losses', 'Inspect accessible steam traps, leaks, insulation and condensate return.'),
      prompt('heat-recovery', 'Identify hot exhaust, product, condensate or cooling streams worth investigating.'),
    ],
    dataPrompts: ['Equipment', 'Fuel/source', 'Rated kW', 'Temperature °C', 'Pressure bar', 'Run hours'],
    safetyNote: 'Remain outside controlled hot-work and pressure-system areas unless escorted and authorised.',
  },
  specialist: {
    id: 'specialist',
    label: 'Catering, laundry & specialist loads',
    description: 'Ovens, washers, dryers, sterilisation, pools and other sector-specific equipment.',
    prompts: [
      prompt('specialist-assets', 'Identify the highest-use specialist equipment and its control arrangements.'),
      prompt('specialist-cycle', 'Record cycle frequency, loading, preheat and idle practice.'),
      prompt('specialist-extraction', 'Check linked extraction, hot water, steam or cooling loads.'),
      prompt('specialist-maintenance', 'Ask about faults, cleaning, maintenance and replacement plans.'),
    ],
    dataPrompts: ['Equipment', 'Quantity', 'Rated kW', 'Cycles/day', 'Hours', 'Setpoint'],
  },
  power: {
    id: 'power',
    label: 'Power quality & electrical',
    description: 'Transformers, power factor correction, harmonics concerns and distribution condition.',
    prompts: [
      prompt('transformers', 'Identify transformers, loading information and redundant energised units.'),
      prompt('pfc', 'Inspect visible power factor correction status and alarms.'),
      prompt('quality', 'Ask about nuisance trips, voltage issues, harmonics or overheating.'),
      prompt('distribution', 'Record accessible panel/meter labels and obvious monitoring gaps.'),
    ],
    dataPrompts: ['Equipment', 'Rating kVA', 'Load if known', 'Power factor', 'Alarm/status'],
    safetyNote: 'Do not open electrical panels unless site rules, competence and permits allow it.',
  },
  renewables: {
    id: 'renewables',
    label: 'Renewables, storage & EV charging',
    description: 'On-site generation, batteries, CHP, generators and vehicle charging.',
    prompts: [
      prompt('generation', 'Identify generation/storage assets, metering and current operating status.'),
      prompt('generation-use', 'Ask how generation is used, curtailed, exported or controlled.'),
      prompt('ev-charging', 'Record charger quantity, rating, access, scheduling and observed utilisation.'),
      prompt('replacement', 'Note planned expansion, replacement or electrification projects.'),
    ],
    dataPrompts: ['Technology', 'Capacity kW/kWp', 'Quantity', 'Metered Y/N', 'Operating hours'],
  },
  fleet: {
    id: 'fleet',
    label: 'On-site fleet observations',
    description: 'Only physical vehicle, charging, idling and operating practices visible during the site visit.',
    prompts: [
      prompt('fleet-practice', 'Observe idling, charging, parking and dispatch practices relevant to the site.'),
      prompt('fleet-charging', 'Record available charging and practical constraints for future provision.'),
      prompt('fleet-exclusions', 'Do not turn this into a fleet-data or transport compliance exercise.'),
    ],
    dataPrompts: ['Vehicle/charger type', 'Quantity observed', 'Rating', 'Operating note'],
  },
  highUsers: {
    id: 'high-users',
    label: 'High energy users',
    description: 'A final physical sense-check that the largest visible loads have not been missed.',
    prompts: [
      prompt('top-users', 'Ask the site team which three to five assets or processes they expect to use most.'),
      prompt('coverage', 'Confirm each is represented by an observation or explicitly noted as inaccessible.'),
      prompt('unexpected', 'Record unexpected loads, operating hours or constraints discovered on the walk.'),
    ],
    dataPrompts: ['Asset/process', 'Rated kW', 'Hours', 'Load/duty', 'Reason significant'],
  },
  custom: {
    id: 'custom',
    label: 'Other & custom',
    description: 'Site-specific systems, issues or opportunities not covered elsewhere.',
    prompts: [prompt('custom-observe', 'Record what was observed, why it matters and what should happen next.')],
    dataPrompts: ['Label', 'Value', 'Unit'],
  },
}

const commonQuickChecks = [
  prompt('unoccupied-operation', 'Lights, heating, cooling or ventilation operating in unoccupied areas?'),
  prompt('openings', 'Doors, loading bays or windows open while heating or cooling is running?'),
  prompt('visible-faults', 'Obvious leaks, damaged insulation or equipment faults?'),
  prompt('overrides', 'Controls overridden from the intended schedule?'),
  prompt('idle-equipment', 'Motors, fans, pumps or process lines running when not required?'),
  prompt('shutdown-awareness', 'Staff unclear on shutdown or start-up procedures?'),
  prompt('meter-understanding', 'Meter labels or sub-meter boundaries not understood?'),
  prompt('unmetered-users', 'Significant energy users not covered by available metering?'),
  prompt('recovery-electrification', 'Heat recovery or electrification worth investigating?'),
  prompt('constraints', 'Safety, access or production constraints affecting recommendations?'),
]

const buildingsOrder = [
  'metering', 'management', 'fabric', 'lighting', 'heating', 'cooling', 'ventilation',
  'hotWater', 'controls', 'ict', 'motors', 'specialist', 'power', 'renewables', 'fleet', 'highUsers', 'custom',
]

const industrialOrder = [
  'metering', 'management', 'process', 'motors', 'compressedAir', 'processHeat', 'cooling',
  'ventilation', 'controls', 'power', 'renewables', 'fabric', 'lighting', 'hotWater', 'specialist',
  'ict', 'fleet', 'highUsers', 'custom',
]

const makeTemplate = (
  id: TemplateKind,
  label: string,
  description: string,
  order: string[],
): ChecklistTemplate => ({
  id,
  version: 1,
  label,
  description,
  categories: order.map((key) => shared[key]),
  quickChecks: commonQuickChecks,
})

export const templates: Record<TemplateKind, ChecklistTemplate> = {
  buildings: makeTemplate(
    'buildings',
    'Standard buildings',
    'Offices, retail, education, healthcare, hotels and other fixed-premises buildings.',
    buildingsOrder,
  ),
  industrial: makeTemplate(
    'industrial',
    'Industrial / manufacturing',
    'Manufacturing, warehousing and process-led sites with motor, air, heat and production systems.',
    industrialOrder,
  ),
  blank: {
    id: 'blank',
    version: 1,
    label: 'Blank / custom',
    description: 'A minimal visit for unusual sites. Add observations without a prescribed system list.',
    categories: [shared.metering, shared.management, shared.highUsers, shared.custom],
    quickChecks: commonQuickChecks,
  },
}

export const openingPrompts = [
  'Main activities, processes and products or services',
  'Recent changes to building, equipment, occupancy or production',
  'Known energy issues, comfort complaints or recurring faults',
  'Planned projects, maintenance or equipment replacement',
  'Energy responsibilities, operating practices and shutdown arrangements',
  'People to speak to and records or controls to make available',
]
